

















































import { defineComponent, reactive, computed, ref } from "@vue/composition-api";
import Layout from "@/components/Atoms/Layout/index.vue";
import PageHeader from "@/components/Molecules/PageHeader/index.vue";
import { Props } from "@/components/Molecules/ListTable/index.vue";
import TableFooter from "@/components/Molecules/TableFooter/index.vue";
import LoadingView from "@/components/Molecules/LoadingView/index.vue";
import VideoBundleUnlockingLogsCSVDownloadButton from "./Organisms/VideoBundleUnlockingLogsCSVDownloadButton.vue";
import VideoBundleUnlockingLogsTable from "./Organisms/VideoBundleUnlockingLogsTable.vue";
import VideoBundleUnlockingLogsSearchForm from "./Organisms/VideoBundleUnlockingLogsSearchForm.vue";
import { useRequest } from "@/helpers/hooks/useRequest";
import {
  getVideoBundleUnlockingLogs,
  getVideoBundleUnlockingLogSearchOptions
} from "@/api";
import { ApiRequest } from "@/api/types";
import {
  PRIVILEGE_ENTRIES_DOWNLOAD,
  hasPrivilege
} from "@/helpers/privilege-helper";
import {
  FilterState,
  FilterType
} from "@/components/Pages/VideoBundleUnlockingLogs/Organisms/types";
import HelpLink from "@/components/Atoms/HelpLink/index.vue";

export default defineComponent({
  name: "VideoBundleUnlockingLogs",
  components: {
    Layout,
    PageHeader,
    TableFooter,
    LoadingView,
    VideoBundleUnlockingLogsCSVDownloadButton,
    VideoBundleUnlockingLogsTable,
    VideoBundleUnlockingLogsSearchForm,
    HelpLink
  },
  setup() {
    // MEMO: こういう情報、外に出してもいいかもしれない
    const headers = reactive<Props["headers"]>([
      {
        name: "ユーザー名",
        subHeaders: [{ name: "識別コード" }, { name: "カナ" }]
      },
      {
        name: "大学名",
        subHeaders: [{ name: "卒年" }, { name: "学部・学年" }]
      },
      { name: "エントリー動画" },
      { name: "エントリーチャプター" },
      { name: "エントリー日時", sortParam: "unlocked_at", order: "desc" }
    ]);

    /** ========================================
     * フィルタ
     * ========================================*/
    const filterState = reactive<FilterState>({
      searchText: "",
      universities: [],
      graduateYears: [],
      grades: [],
      videoBundleTitles: [],
      videoChapterTitles: [],
      entryDate: { from: "", to: "" }
    });

    const filterList: {
      label: string;
      name: Exclude<keyof typeof filterState, "searchText">;
      type: FilterType;
    }[] = [
      { label: "大学", name: "universities", type: "multiSelect" },
      { label: "卒年", name: "graduateYears", type: "multiSelect" },
      { label: "学年", name: "grades", type: "multiSelect" },
      { label: "エントリー日時", name: "entryDate", type: "dateRange" },
      {
        label: "エントリー動画",
        name: "videoBundleTitles",
        type: "multiSelect"
      },
      {
        label: "エントリーチャプター",
        name: "videoChapterTitles",
        type: "multiSelect"
      }
    ];

    const onChangeFormValue = (
      name: Exclude<keyof typeof filterState, "entryDate">,
      value: string & string[]
    ) => {
      filterState[name] = value;
    };

    const onChangeFilterEntryDate = (value: { from: string; to: string }) => {
      filterState.entryDate = value;
    };

    const submitSearchForm = async () => {
      currentPage.value = 1;
      await runGetVideoBundleUnlockingLogs();
    };

    const onSearchTextResetButtonClicked = async () => {
      filterState.searchText = "";
      await runGetVideoBundleUnlockingLogs();
    };

    const resetSearchForm = () => {
      filterState.searchText = "";
      filterState.universities = [];
      filterState.graduateYears = [];
      filterState.grades = [];
      filterState.videoBundleTitles = [];
      filterState.videoChapterTitles = [];
      filterState.entryDate = { from: "", to: "" };
    };

    /** ========================================
     * ソート
     * ========================================*/
    const sortOrder = ref<ApiRequest.VideoBundleUnlockingLogs["order"]>("desc");

    const onChangeOrder = async (
      order: ApiRequest.VideoBundleUnlockingLogs["order"],
      headerIndex: number
    ) => {
      const header = headers[headerIndex];

      if (!header) return;

      sortOrder.value = order;
      header.order = order;

      currentPage.value = 1;
      await runGetVideoBundleUnlockingLogs();
    };

    /** ========================================
     * ページネーション
     * ========================================*/
    const currentPage = ref(1);
    const displayCount = ref(25);

    const displayCountSelectList = [
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" }
    ];

    const totalPageCount = computed(() => {
      if (
        !videoBundleUnlockingLogs.value ||
        !videoBundleUnlockingLogs.value.total
      ) {
        return 1;
      }
      return Math.ceil(
        videoBundleUnlockingLogs.value?.total / displayCount.value
      );
    });

    const onChangePage = async (pageNumber: number) => {
      currentPage.value = pageNumber;
      await runGetVideoBundleUnlockingLogs();
    };

    const onChangeDisplayCount = async (count: number) => {
      displayCount.value = count;
      currentPage.value = 1;
      await runGetVideoBundleUnlockingLogs();
    };

    const searchParams = computed<ApiRequest.VideoBundleUnlockingLogs>(() => {
      const params: ApiRequest.VideoBundleUnlockingLogs = {
        search_text: filterState.searchText,
        universities: filterState.universities,
        graduate_years: filterState.graduateYears,
        grades: filterState.grades,
        video_bundle_titles: filterState.videoBundleTitles,
        video_chapter_titles: filterState.videoChapterTitles,
        order: sortOrder.value,
        limit: displayCount.value,
        offset: displayCount.value * (currentPage.value - 1)
      };
      if (filterState.entryDate.from !== "") {
        params.entry_date_from = filterState.entryDate.from;
      }
      if (filterState.entryDate.to !== "") {
        params.entry_date_to = filterState.entryDate.to;
      }

      return params;
    });

    const [
      {
        data: videoBundleUnlockingLogs,
        loading: isVideoBundleUnlockingLogsLoading
      },
      runGetVideoBundleUnlockingLogs
    ] = useRequest(() => getVideoBundleUnlockingLogs(searchParams.value), {
      immediate: true
    });

    /** ========================================
     * ローディング
     * ========================================*/
    const isLoading = computed(() => {
      return !!isVideoBundleUnlockingLogsLoading.value;
    });

    /** ========================================
     * CSVダウンロード
     * ========================================*/
    const hasCsvDownloadPriviledge = hasPrivilege(PRIVILEGE_ENTRIES_DOWNLOAD);

    return {
      headers,
      videoBundleUnlockingLogs,

      searchParams,

      getVideoBundleUnlockingLogSearchOptions,
      filterState,
      filterList,
      onChangeFormValue,
      onChangeFilterEntryDate,
      submitSearchForm,
      onSearchTextResetButtonClicked,
      resetSearchForm,

      sortOrder,
      onChangeOrder,

      currentPage,
      displayCount,
      displayCountSelectList,
      totalPageCount,
      onChangePage,
      onChangeDisplayCount,

      isLoading,

      hasCsvDownloadPriviledge
    };
  }
});
